import React from "react";
import { buildDashboardHomeModel } from "../utils/dashboardHomeData.js";
import { loadPageData } from "./usePageData.js";

export const DASHBOARD_HOME_API_URL = "/api/dashboard/home";

function resolveDashboardHomeErrorMessage(error) {
  return error instanceof Error && error.message.trim()
    ? error.message.trim()
    : "Impossible de charger les indicateurs du dashboard.";
}

export default function useDashboardHomeData(initialReadModel = null) {
  const [readModel, setReadModel] = React.useState(initialReadModel);
  const [status, setStatus] = React.useState(initialReadModel ? "ready" : "loading");
  const [errorMessage, setErrorMessage] = React.useState("");
  const requestIdRef = React.useRef(0);
  // « 30 dispatchs » reads 60 dispatchs: it is requested only when the tab is opened.
  const [month, setMonth] = React.useState({ readModel: null, status: "idle" });

  const loadDashboardHome = React.useCallback(async ({ force = false } = {}) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setStatus("loading");
    setErrorMessage("");

    try {
      const nextReadModel = await loadPageData(DASHBOARD_HOME_API_URL, {
        acceptableStatusCodes: [400],
        force,
      });
      if (requestIdRef.current !== requestId) {
        return null;
      }

      setReadModel(nextReadModel);
      setMonth({ readModel: null, status: "idle" });
      setStatus("ready");
      return nextReadModel;
    } catch (error) {
      if (requestIdRef.current !== requestId) {
        return null;
      }

      setErrorMessage(resolveDashboardHomeErrorMessage(error));
      setStatus("error");
      return null;
    }
  }, []);

  React.useEffect(() => {
    if (initialReadModel) {
      requestIdRef.current += 1;
      setReadModel(initialReadModel);
      setErrorMessage("");
      setStatus("ready");
      return undefined;
    }

    void loadDashboardHome();
    return () => {
      requestIdRef.current += 1;
    };
  }, [initialReadModel, loadDashboardHome]);

  const retry = React.useCallback(() => loadDashboardHome({ force: true }), [loadDashboardHome]);
  const model = React.useMemo(() => buildDashboardHomeModel(readModel), [readModel]);
  const loadMonth = React.useCallback(async () => {
    if (model.analytics.monthLoaded || month.status === "loading" || month.status === "ready") return;
    setMonth({ readModel: null, status: "loading" });
    try {
      setMonth({ readModel: await loadPageData(`${DASHBOARD_HOME_API_URL}?period=month`, { acceptableStatusCodes: [400] }), status: "ready" });
    } catch {
      setMonth({ readModel: null, status: "error" });
    }
  }, [model.analytics.monthLoaded, month.status]);
  const monthModel = React.useMemo(() => (model.analytics.monthLoaded ? model : month.readModel ? buildDashboardHomeModel(month.readModel) : null),
    [model, month.readModel]);

  return {
    errorMessage,
    isLoading: status === "loading",
    loadMonth,
    model,
    monthModel,
    monthStatus: model.analytics.monthLoaded ? "ready" : month.status,
    retry,
    status,
  };
}
